import { instance as boompayClient } from "api/apiBase";
import { inboxClient } from "../../inbox/api/client";
import type {
  AssignedPropertiesPayload,
  BuyPhoneNumberPayload,
  ImportFromTwilioPayload,
  PartnerUserOption,
  PhoneNumber,
  PhoneNumbersListResponse,
  PropertyPhoneDefault,
  SearchAvailableParams,
  SearchAvailableResponse,
  UpdatePhoneNumberPayload
} from "./types";

const PORTAL_BASE = "/portal/v1/provisioned_phone_numbers";

// Property-default endpoints still live on comm-service direct API
// (not exposed by boompay-api PR 4079).
const COMM_BASE = "/api/portal/provisioned_phone_numbers";
const DEFAULT_PER_PAGE = 10;

export const phoneNumbersApi = {
  list: async (
    page = 1,
    perPage = DEFAULT_PER_PAGE,
    search?: string,
    propertyIds?: number[],
    // CRM-811: scope the "call from" list to a lead's / contact's property on the
    // server (which also fail-closes to the user's accessible properties).
    scope?: { leadId?: number; contactId?: string | number },
    // CRM-1356: the Phone Numbers screen asks for the per-number ring count; it
    // costs a few queries per number, so pickers leave it off.
    options?: { withInboundRecipients?: boolean }
  ) => {
    const { data } = await boompayClient.get<PhoneNumbersListResponse>(
      PORTAL_BASE,
      {
        params: {
          page,
          per_page: perPage,
          ...(search ? { search } : {}),
          ...(propertyIds?.length ? { property_ids: propertyIds } : {}),
          ...(scope?.leadId ? { lead_id: scope.leadId } : {}),
          ...(scope?.contactId ? { contact_id: scope.contactId } : {}),
          ...(options?.withInboundRecipients
            ? { with_inbound_recipients: true }
            : {})
        }
      }
    );
    return data;
  },

  listAll: async (options?: {
    propertyIds?: number[];
    scope?: { leadId?: number; contactId?: string | number };
  }) => {
    const { data } = await boompayClient.get<PhoneNumbersListResponse>(
      PORTAL_BASE,
      {
        params: {
          skip_pagination: true,
          ...(options?.propertyIds?.length
            ? { property_ids: options.propertyIds }
            : {}),
          ...(options?.scope?.leadId ? { lead_id: options.scope.leadId } : {}),
          ...(options?.scope?.contactId
            ? { contact_id: options.scope.contactId }
            : {})
        }
      }
    );
    return data;
  },

  searchAvailable: async (params: SearchAvailableParams) => {
    const { data } = await boompayClient.get<SearchAvailableResponse>(
      `${PORTAL_BASE}/search_available`,
      {
        params: {
          search: params.search,
          matchTo: params.matchTo,
          pageSize: params.pageSize,
          ...(params.country ? { country: params.country } : {})
        }
      }
    );
    return data;
  },

  buy: async (payload: BuyPhoneNumberPayload) => {
    const { data } = await boompayClient.post<PhoneNumber>(
      `${PORTAL_BASE}/buy`,
      payload
    );
    return data;
  },

  // Import endpoint lives on comm-service direct API
  // (not exposed by boompay-api as of PR #42).
  importFromTwilio: async (payload: ImportFromTwilioPayload) => {
    const { data } = await inboxClient.post<PhoneNumber>(
      `${COMM_BASE}/import`,
      payload
    );
    return data;
  },

  // Mutation endpoints expect the comm-service UUID (NOT the local DB id).
  update: async (commId: string, payload: UpdatePhoneNumberPayload) => {
    const { data } = await boompayClient.patch<PhoneNumber>(
      `${PORTAL_BASE}/${commId}`,
      payload
    );
    return data;
  },

  remove: async (commId: string, newDefaultPhoneNumberId?: string) => {
    await boompayClient.delete(`${PORTAL_BASE}/${commId}`, {
      params: newDefaultPhoneNumberId
        ? { new_default_phone_number_id: newDefaultPhoneNumberId }
        : undefined
    });
  },

  setDefault: async (commId: string) => {
    const { data } = await boompayClient.put<PhoneNumber>(
      `${PORTAL_BASE}/${commId}/default`
    );
    return data;
  },

  listPropertyDefaults: async () => {
    const { data } = await inboxClient.get<PropertyPhoneDefault[]>(
      `${COMM_BASE}/properties/defaults`
    );
    return data;
  },

  getPropertyDefault: async (propertyId: number) => {
    const { data } = await inboxClient.get<PropertyPhoneDefault | null>(
      `${COMM_BASE}/properties/${propertyId}/default`
    );
    return data;
  },

  setPropertyDefault: async (propertyId: number, phoneNumberId: string) => {
    const { data } = await inboxClient.put(
      `${COMM_BASE}/properties/${propertyId}/default`,
      { phone_number_id: phoneNumberId }
    );
    return data;
  },

  removePropertyDefault: async (propertyId: number) => {
    await inboxClient.delete(`${COMM_BASE}/properties/${propertyId}/default`);
  },

  updateAssignedProperties: async (
    commId: string,
    payload: AssignedPropertiesPayload
  ) => {
    const { data } = await boompayClient.put<PhoneNumber>(
      `${PORTAL_BASE}/${commId}/properties`,
      payload
    );
    return data;
  },

  // Partner users, used to populate call-tree option targets (CRM-1166).
  listUsers: async (): Promise<PartnerUserOption[]> => {
    const { data } = await boompayClient.get<
      PartnerUserOption[] | { items: PartnerUserOption[] }
    >("/portal/v1/users", { params: { per_page: 200 } });
    const items = Array.isArray(data) ? data : (data.items ?? []);
    return items
      .filter((u) => u.receives_inbound_calls !== false)
      .map((u) => ({ id: u.id, name: u.name }));
  }
};
